
import React, { useEffect, useState } from 'react';
import { ApiKey, UsageLog, getUsageLogs } from '../../services/apiService';
import { ChartEntry } from '../../types/chart';
import ChartCard from '../common/ChartCard';


const InputTokensIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12H3"/>
    <path d="M8 5l-5 7 5 7"/>
  </svg>
);

const OutputTokensIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h18"/>
    <path d="M16 5l5 7-5 7"/>
  </svg>
);

const ActionsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const ChartLineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18"/>
    <path d="m3 9 4 4 8-8 5 5"/>
  </svg>
);

const ChartBarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
    <line x1="3" y1="20" x2="21" y2="20"/>
  </svg>
);

const SetupInProgressIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const EmptyDataIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
    <line x1="8" y1="21" x2="16" y2="21"></line>
    <line x1="12" y1="17" x2="12" y2="21"></line>
    <path d="M7 9h2M15 9h2M8 13h8"></path>
  </svg>
);

interface AnalyticsTabProps {
  apiKey: ApiKey | null;
  onNavigateToTab?: (tabName: string) => void;
}

export default function AnalyticsTab({ apiKey, onNavigateToTab }: AnalyticsTabProps) {
  const [dailyTokens, setDailyTokens] = useState<ChartEntry[]>([]);
  const [topActions, setTopActions] = useState<ChartEntry[]>([]);
  const [totalInput, setTotalInput] = useState(0);
  const [totalOutput, setTotalOutput] = useState(0);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d
  const [hasUsageData, setHasUsageData] = useState(false);

  useEffect(() => {
    async function loadAnalytics() {
      setLoading(true);
      try {

        if (!apiKey) {
          setLoading(false);
          return;
        }
        
        const logs: UsageLog[] = await getUsageLogs(apiKey.user_id);
        

        setHasUsageData(logs.length > 0);


        const now = new Date();
        const daysToFilter = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        const cutoffDate = new Date(now.setDate(now.getDate() - daysToFilter));
        
        const filteredLogs = logs.filter(log => new Date(log.created_at) >= cutoffDate);

        const dailyMap: Record<string, number> = {};
        const actionMap: Record<string, number> = {};
        let input = 0, output = 0;

        for (const log of filteredLogs) {
          const date = new Date(log.created_at).toLocaleDateString('en-US', {
            month: 'short', 
            day: 'numeric'
          });
          const inT = log.input_tokens || 0;
          const outT = log.output_tokens || 0;
          input += inT;
          output += outT;
          dailyMap[date] = (dailyMap[date] || 0) + inT + outT;

          const name = typeof log.action === 'string' 
            ? log.action 
            : (log.action ? JSON.stringify(log.action).slice(0, 30) : 'unknown');
          actionMap[name] = (actionMap[name] || 0) + 1;
        }

        setTotalInput(input);
        setTotalOutput(output);


        const daily: ChartEntry[] = Object.entries(dailyMap)
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => {
            return new Date(a.label).getTime() - new Date(b.label).getTime();
          });

        const actions: ChartEntry[] = Object.entries(actionMap)
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);

        setDailyTokens(daily);
        setTopActions(actions);
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    }

    loadAnalytics();
  }, [apiKey, timeRange]);


  const handleGoToOverview = () => {
    if (onNavigateToTab) {
      onNavigateToTab('overview');
    }
  };


  if (!apiKey) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '70vh',
        textAlign: 'center',
        padding: '0 2rem'
      }}>
        <SetupInProgressIcon />
        <h2 style={{
          marginTop: '1.5rem',
          marginBottom: '1rem',
          fontSize: '1.5rem',
          fontWeight: '500'
        }}>
          Complete your setup first
        </h2>
        <p style={{
          color: 'var(--text-muted)',
          maxWidth: '500px',
          marginBottom: '1.5rem'
        }}>
          Analytics will be available after you generate your API key and start using the Abra platform.
        </p>
        <button 
          className="button-primary"
          onClick={handleGoToOverview}
        >
          Go to Overview
        </button>
      </div>
    );
  }


  if (!loading && !hasUsageData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '70vh',
        textAlign: 'center',
        padding: '0 2rem'
      }}>
        <EmptyDataIcon />
        <h2 style={{
          marginTop: '1.5rem',
          marginBottom: '1rem',
          fontSize: '1.5rem',
          fontWeight: '500'
        }}>
          No analytics data yet
        </h2>
        <p style={{
          color: 'var(--text-muted)',
          maxWidth: '500px',
          marginBottom: '1.5rem'
        }}>
          Check out the integration guide in the Overview tab to start integrating Abra and sending API requests. Analytics will populate once you start using the platform.
        </p>
        <button 
          className="button-primary"
          onClick={handleGoToOverview}
        >
          Go to Integration Guide
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-tab">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-title">
            <InputTokensIcon />
            Total Input Tokens
          </div>
          <div className="stat-value">{totalInput.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-title">
            <OutputTokensIcon />
            Total Output Tokens
          </div>
          <div className="stat-value">{totalOutput.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-title">
            <ActionsIcon />
            Top Actions Count
          </div>
          <div className="stat-value">{topActions.length}</div>
        </div>
      </div>

      {}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        {['7d', '30d', '90d'].map((range) => (
          <button
            key={range}
            className={`chart-filter ${timeRange === range ? 'active' : ''}`}
            onClick={() => setTimeRange(range)}
          >
            {range === '7d' ? 'Last 7 Days' : range === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
          </button>
        ))}
      </div>

      <div className="charts-grid">
        <ChartCard
          title="Daily Token Usage"
          data={dailyTokens}
          icon={<ChartLineIcon />}
          type="line"
          loading={loading}
        />
        <ChartCard
          title="Most Used Actions"
          data={topActions}
          icon={<ChartBarIcon />}
          type="bar"
          loading={loading}
        />
      </div>
    </div>
  );
}